import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { InputNumberProps, TableProps } from 'antd';
import axios from 'axios';
import useSWR from 'swr';
import { DataType } from '../pages/inquiry/InquiryItems';

export interface WithInquiryItemsLogicProps {
    loading: boolean;
    handleButtonClick: (values: any) => void;
    handleRowDoubleClick: (record: DataType) => void;
    onChange: (value: string) => void;
    onChange1: InputNumberProps['onChange'];
    onChange2: InputNumberProps['onChange'];
    selectedRowKeys: React.Key[];
    handleRowSelectionChange: (newSelectedRowKeys: React.Key[]) => void;
    modelOptions: { value: string; label: string; }[] | undefined;
    brandOptions: { value: string; label: string; }[] | undefined;
    companyOptions: { value: string; label: string; }[] | undefined;
    itemClassOptions: { value: string; label: string; }[] | undefined;
    warehouseGroupOptions: { value: string; label: string; }[] | undefined;
    locationCodeOptions: { value: string; label: string; }[] | undefined;
    itemCodeSearch: string;
    handleItemCodeSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    searchResults: DataType[];
    itemNameSearch: string;
    handleItemNameSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    pagination: { current: number; pageSize: number; total: number };
    handleTableChange: (pagination: any, filters: any, sorter: any) => void;
}


interface CompanyGroup {
    id: number;
    code: string;
    description: string;
}

interface SparepartModel {
    code: string;
    description: string;
}

interface SparepartBrand {
    code: string;
    description: string;
}

interface SparepartItemClass {
    code: string;
    description: string;
}

interface WarehouseGroup {
    id: number;
    warehouse_code: string;
    warehouse_name: string;
}

interface SparepartLocationCode {
    id: number;
    code_rack: string;
    status: string;
}

const fetcher = (url: string) => axios.get(url).then(res => res.data);
const searchFetcher = ([url, params]: [string, { item_code?: string; item_name?: string }]) =>
    axios.get(url, { params }).then(res => res.data);

const withInquiryItemsLogic = <P extends object>(
    WrappedComponent: React.ComponentType<P & WithInquiryItemsLogicProps>
) => {
    const EnhancedComponent: React.FC<P> = (props) => {
        const navigate = useNavigate();

        const [loading, setLoading] = useState(false);
        const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
        const [itemCodeSearch, setItemCodeSearch] = useState<string>('');
        const [searchResults, setSearchResults] = useState<DataType[]>([]);
        const [itemNameSearch, setItemNameSearch] = useState<string>('');
        const [currentSearchParams, setCurrentSearchParams] = useState<any>(null);
        const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
        const [sortField, setSortField] = useState<string>('id');
        const [sortOrder, setSortOrder] = useState<string>('ASC');

        const { data: models, error: modelsError } = useSWR<SparepartModel[]>(
            `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/models`,
            fetcher
        );

        const { data: brands, error: brandsError } = useSWR<SparepartBrand[]>(
            `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/brands`,
            fetcher
        );

        const { data: companies, error: companiesError } = useSWR<CompanyGroup[]>(
            `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/companies`,
            fetcher
        );

        const { data: itemClasses, error: itemClassesError } = useSWR<SparepartItemClass[]>(
            `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/item_classes`,
            fetcher
        );

        const { data: warehouseGroups, error: warehouseGroupsError } = useSWR<WarehouseGroup[]>(
            `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/warehouse_groups`,
            fetcher
        );

        const { data: locationCodes, error: locationCodesError } = useSWR<SparepartLocationCode[]>(
            `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/location_codes`,
            fetcher
        );

        const fetchResults = async (
            params: any,
            page: number,
            limit: number,
            sortBy: string,
            sortOrder: string
        ) => {
            setLoading(true);
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/search`, {
                    params: {
                        ...params,
                        page,
                        limit,
                        sortBy,
                        sortOrder,
                    }
                });
                setSearchResults(response.data.data || []);
                setPagination({
                    current: page,
                    pageSize: limit,
                    total: response.data.total || 0,
                });
            } catch (error) {
                console.error("Failed to fetch search results:", error);
            } finally {
                setLoading(false);
            }
        };

        const { data, error, isLoading } = useSWR<DataType[]>(
            currentSearchParams ? [`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/search`, currentSearchParams] : null,
            searchFetcher
        );

        const processedSearchResults = React.useMemo(() => {
            if (!searchResults) {
                return [];
            }
            const processedData = searchResults.map(item => {
                const salesPriceAsNumber = typeof item.sales_price === 'number' ? item.sales_price : parseFloat(item.sales_price as any);
                const formattedPrice = !isNaN(salesPriceAsNumber) ? salesPriceAsNumber.toLocaleString('en-US') : (item.sales_price != null ? String(item.sales_price) : undefined);
                const processedItem: DataType = {
                    ...item,
                    warehouse_group: item.warehouse_group || (item as any).warehouse_code,
                    formatted_sales_price: formattedPrice,
                };
                if (processedItem.children && processedItem.children.length > 0) {
                    const childrenWithFormattedPrice = processedItem.children.map(child => {
                        const childSalesPriceAsNumber = typeof child.sales_price === 'number' ? child.sales_price : parseFloat(child.sales_price as any);
                        const formattedChildPrice = !isNaN(childSalesPriceAsNumber) ? childSalesPriceAsNumber.toLocaleString('en-US') : (child.sales_price != null ? String(child.sales_price) : undefined);
                        return {
                            ...child,
                            formatted_sales_price: formattedChildPrice,
                        };
                    });
                    return { ...processedItem, children: childrenWithFormattedPrice };
                } else if (processedItem.children && processedItem.children.length === 0) {
                    const { children, ...rest } = processedItem;
                    return rest as DataType;
                }
                return processedItem;
            });
            return processedData;

        }, [searchResults])

        const modelOptions = React.useMemo(() => {
            if (!models) return [];
            return models.map(model => ({
                value: model.code,
                label: model.description,
            }));
        }, [models]);

        const brandOptions = React.useMemo(() => {
            if (!brands) return [];
            return brands.map(brand => ({
                value: brand.code,
                label: brand.description,
            }));
        }, [brands]);

        const companyOptions = React.useMemo(() => {
            if (!companies) return [];
            return companies.map(company => ({
                value: company.code,
                label: company.description,
            }));
        }, [companies]);

        const itemClassOptions = React.useMemo(() => {
            if (!itemClasses) return [];
            return itemClasses.map(itemClass => ({
                value: itemClass.code,
                label: itemClass.description,
            }));
        }, [itemClasses]);

        const warehouseGroupOptions = React.useMemo(() => {
            if (!warehouseGroups) return [];
            return warehouseGroups.map(group => ({
                value: group.warehouse_code,
                label: group.warehouse_name,
            }));
        }, [warehouseGroups]);

        const locationCodeOptions = React.useMemo(() => {
            if (!locationCodes) return [];
            return locationCodes.map(location => ({
                value: location.code_rack,
                label: location.code_rack,
            }));
        }, [locationCodes]);

        const onChange = (value: string) => { };
        const onChange1: InputNumberProps['onChange'] = (value) => { };
        const onChange2: InputNumberProps['onChange'] = (value) => { };

        const handleItemCodeSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
            setItemCodeSearch(event.target.value);
        }, []);

        const handleItemNameSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
            setItemNameSearch(event.target.value);
        }, []);

        const handleButtonClick = async (values: any) => {
            const filteredParams = Object.keys(values).reduce((acc: any, key) => {
                if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
                    acc[key] = values[key];
                }
                return acc;
            }, {});

            setCurrentSearchParams(filteredParams);
            await fetchResults(filteredParams, 1, pagination.pageSize, sortField, sortOrder);
        };

        const handleTableChange = async (newPagination: any, filters: any, sorter: any) => {
            const newPage = newPagination.current || 1;
            const newPageSize = newPagination.pageSize || 10;
            let newSortField = 'id';
            let newSortOrder = 'ASC';

            if (sorter && sorter.field) {
                newSortField = sorter.field;
                newSortOrder = sorter.order === 'descend' ? 'DESC' : 'ASC';
            }

            setSortField(newSortField);
            setSortOrder(newSortOrder);

            await fetchResults(currentSearchParams, newPage, newPageSize, newSortField, newSortOrder);
        };

        const handleRowDoubleClick = async (record: DataType) => {
            try {
                const params = {
                    item_code: record.item_code,
                    warehouse_group: record.warehouse_group,
                };

                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/details`, { params });
                const details = response.data;

                const warehouseOption = warehouseGroupOptions?.find(opt => opt.value === record.warehouse_group);
                const warehouse_name = warehouseOption ? warehouseOption.label : '';

                const navigationState = {
                    item_code: record.item_code,
                    item_name: record.item_name,
                    average_cost: details.average_cost,
                    on_order_qty: details.on_order_qty,
                    warehouse_code: record.warehouse_group,
                    warehouse_name: warehouse_name,
                    location_code: details.location_code,
                    location_name: details.location_name,
                    sales_price: record.sales_price,
                };

                navigate('/dashboard/inquiryinfo', { state: navigationState });

            } catch (error) {
                console.error("Failed to fetch item details:", error);
            }
        };

        const handleRowSelectionChange = (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        };

        const logicProps: WithInquiryItemsLogicProps = {
            loading,
            handleButtonClick,
            handleRowDoubleClick,
            onChange,
            onChange1,
            onChange2,
            selectedRowKeys,
            handleRowSelectionChange,
            modelOptions,
            brandOptions,
            companyOptions,
            itemClassOptions,
            warehouseGroupOptions,
            locationCodeOptions,
            itemCodeSearch,
            handleItemCodeSearchChange,
            itemNameSearch,
            handleItemNameSearchChange,
            searchResults: processedSearchResults,
            pagination,
            handleTableChange,
        };

        return <WrappedComponent {...props as P} {...logicProps} />;
    };

    return EnhancedComponent;
};

export default withInquiryItemsLogic;